import React, { useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import {
  applyManualTimeInputFormat,
  isCompleteManualTimeInput,
  parseManualTimeToDate,
} from '../../../../../utils/format-manual-time-input';

interface ManualTimeCalendarProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  style?: React.CSSProperties;
  panelStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}

export const ManualTimeCalendar: React.FC<ManualTimeCalendarProps> = ({
  value,
  onChange,
  style,
  panelStyle,
  inputStyle,
}) => {
  const calendarRef = useRef<Calendar>(null);
  const isEditingRef = useRef(false);
  const editTextRef = useRef('');
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  valueRef.current = value;
  onChangeRef.current = onChange;

  useEffect(() => {
    const attachHandlers = () => {
      const input = calendarRef.current?.getInput();
      if (!input || input.dataset.manualTimeHandlerAttached === 'true') {
        return;
      }

      input.dataset.manualTimeHandlerAttached = 'true';

      const handleFocus = () => {
        isEditingRef.current = true;
        editTextRef.current = input.value;
      };

      const handleInput = () => {
        const formatted = applyManualTimeInputFormat(input);
        editTextRef.current = formatted;

        if (isCompleteManualTimeInput(formatted)) {
          const parsed = parseManualTimeToDate(formatted, valueRef.current);
          if (parsed) {
            onChangeRef.current(parsed);
          }
        }
      };

      const handleBlur = () => {
        isEditingRef.current = false;

        const formatted = applyManualTimeInputFormat(input);
        editTextRef.current = formatted;

        if (isCompleteManualTimeInput(formatted)) {
          const parsed = parseManualTimeToDate(formatted, valueRef.current);
          onChangeRef.current(parsed ?? null);
          return;
        }

        if (!formatted) {
          onChangeRef.current(null);
        }
      };

      input.addEventListener('focus', handleFocus);
      input.addEventListener('input', handleInput);
      input.addEventListener('blur', handleBlur);
    };

    attachHandlers();

    const timer = window.setTimeout(attachHandlers, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!isEditingRef.current) {
      return;
    }

    const input = calendarRef.current?.getInput();
    if (input && editTextRef.current !== input.value) {
      input.value = editTextRef.current;
    }
  });

  return (
    <Calendar
      ref={calendarRef}
      style={style}
      panelStyle={panelStyle}
      value={value}
      inputStyle={inputStyle}
      onChange={(e) => onChange((e.value as Date | null) ?? null)}
      timeOnly
      hourFormat="24"
      showIcon
      icon={() => <i className="pi pi-clock" />}
    />
  );
};
